import React from "react";
import { getBaseUrl } from '../../utils/urlHelper';

function ReferInfo({ referrals, user, loading }) {
    // Get referral statistics from API response structure
    const stats = referrals?.stats || {};
    const referralList = referrals?.referrals || [];
    
    const totalReferrals = stats.total || 0;
    const activeReferrals = stats.active || 0;
    const kycVerifiedReferrals = stats.kycVerified || 0;
    
    // Get referral code from user profile
    const referralCode = user?.referalCode || user?.referralCode || '';
    const publicUrl = getBaseUrl();
    const referralLink = referralCode ? `${window.location.origin}${publicUrl}/sign-up?ref=${referralCode}` : '';

    const handleCopyCode = () => {
        if (referralCode) {
            navigator.clipboard.writeText(referralCode);
            alert('Referral code copied to clipboard!');
        }
    };

    const handleCopyLink = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            alert('Referral link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Referral Code Section */}
            <div className="card mb-3">
                <div className="card-header py-3 bg-transparent border-bottom">
                    <h6 className="mb-0 fw-bold">Your Referral Code</h6>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-lg-6 col-md-12">
                            <label className="form-label fw-semibold d-block mb-2">Referral Code</label>
                            <div className="input-group">
                                <input 
                                    type="text" 
                                    className="form-control form-control-lg" 
                                    value={referralCode || 'N/A'} 
                                    readOnly
                                />
                                <button 
                                    className="btn btn-primary" 
                                    type="button" 
                                    onClick={handleCopyCode}
                                    disabled={!referralCode}
                                    title="Copy referral code"
                                >
                                    <i className="icofont-copy me-1"></i> Copy
                                </button>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-12">
                            <label className="form-label fw-semibold d-block mb-2">Referral Link</label>
                            <div className="input-group">
                                <input 
                                    type="text" 
                                    className="form-control form-control-lg" 
                                    value={referralLink || 'N/A'} 
                                    readOnly
                                />
                                <button 
                                    className="btn btn-primary" 
                                    type="button" 
                                    onClick={handleCopyLink}
                                    disabled={!referralLink}
                                    title="Copy referral link"
                                >
                                    <i className="icofont-copy me-1"></i> Copy
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="row g-3 mb-3">
                <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <div className="text-muted small text-uppercase mb-1">Total Referrals</div>
                                    <h3 className="mb-0 fw-bold">{totalReferrals}</h3>
                                </div>
                                <div className="text-primary" style={{ fontSize: '2.5rem' }}>
                                    <i className="icofont-users-alt"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <div className="text-muted small text-uppercase mb-1">Active Referrals</div>
                                    <h3 className="mb-0 fw-bold text-success">{activeReferrals}</h3>
                                </div>
                                <div className="text-success" style={{ fontSize: '2.5rem' }}>
                                    <i className="icofont-check-circled"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="card h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <div className="text-muted small text-uppercase mb-1">KYC Verified</div>
                                    <h3 className="mb-0 fw-bold text-info">{kycVerifiedReferrals}</h3>
                                </div>
                                <div className="text-info" style={{ fontSize: '2.5rem' }}>
                                    <i className="icofont-verification-check"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Referral List */}
            <div className="card">
                <div className="card-header py-3 bg-transparent border-bottom">
                    <h6 className="mb-0 fw-bold">Referral List</h6>
                </div>
                <div className="card-body">
                    {referralList && referralList.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Mobile Number</th>
                                        <th>Joined Date</th>
                                        <th>Status</th>
                                        <th>KYC Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referralList.map((referral, index) => {
                                        // Get KYC status from various possible field names
                                        const kycStatus = referral.kycStatus || referral.kyc?.status || (referral.isKycVerified || referral.kycVerified ? 'verified' : null) || 'not_submitted';
                                        
                                        const getKycStatusBadge = (status) => {
                                            const statusLower = (status || '').toLowerCase();
                                            if (statusLower === 'verified' || statusLower === 'approved') {
                                                return { class: 'bg-success', text: 'Verified' };
                                            } else if (statusLower === 'pending' || statusLower === 'under_review') {
                                                return { class: 'bg-warning', text: 'Pending' };
                                            } else if (statusLower === 'rejected' || statusLower === 'failed') {
                                                return { class: 'bg-danger', text: 'Rejected' };
                                            } else {
                                                return { class: 'bg-secondary', text: 'Not Submitted' };
                                            }
                                        };
                                        
                                        const kycBadge = getKycStatusBadge(kycStatus);
                                        
                                        return (
                                            <tr key={referral._id || referral.id || index}>
                                                <td className="fw-semibold">{referral.name || 'N/A'}</td>
                                                <td>{referral.email || 'N/A'}</td>
                                                <td>{referral.mobileNumber || 'N/A'}</td>
                                                <td>
                                                    {referral.joinedAt
                                                        ? new Date(referral.joinedAt).toLocaleDateString('en-US', { 
                                                            year: 'numeric', 
                                                            month: 'short', 
                                                            day: 'numeric' 
                                                        })
                                                        : 'N/A'}
                                                </td>
                                                <td>
                                                    <span className={`badge ${
                                                        referral.status === 'active' ? 'bg-success' : 
                                                        referral.status === 'blocked' ? 'bg-danger' : 
                                                        referral.status === 'inactive' ? 'bg-warning' : 
                                                        'bg-secondary'
                                                    }`}>
                                                        {referral.status ? referral.status.charAt(0).toUpperCase() + referral.status.slice(1) : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge ${kycBadge.class}`}>
                                                        {kycBadge.text}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <i className="icofont-users-alt fs-1 text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-0">No referrals yet. Share your referral code to invite friends!</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ReferInfo;